import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import { RefreshCw, Truck, Mail, Save, X, Send, PackageCheck } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUSES = [
  { id: 'pendiente_comprar', label: 'Pendiente de comprar', color: 'bg-amber-100 text-amber-700' },
  { id: 'comprado', label: 'Comprado al proveedor', color: 'bg-blue-100 text-blue-700' },
  { id: 'enviado', label: 'Enviado', color: 'bg-violet-100 text-violet-700' },
  { id: 'entregado', label: 'Entregado', color: 'bg-emerald-100 text-emerald-700' },
  { id: 'cancelado', label: 'Cancelado', color: 'bg-red-100 text-red-600' },
];
const statusMeta = (id) => STATUSES.find((s) => s.id === id) || STATUSES[0];
const money = (n) => `$${Number(n || 0).toFixed(2)}`;

export default function SupplierOrdersPanel() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [drafts, setDrafts] = useState({});
  const [savingId, setSavingId] = useState(null);
  const [emailModal, setEmailModal] = useState(null); // {orderId, to, subject, body}
  const [sending, setSending] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/supplier-orders`);
      setOrders(r.data || []);
      const d = {};
      (r.data || []).forEach((o) => {
        d[o.id] = {
          supplier_status: o.supplier_status || 'pendiente_comprar',
          supplier_ref: o.supplier_ref || '',
          supplier_tracking: o.supplier_tracking || '',
          supplier_carrier: o.supplier_carrier || '',
          supplier_notes: o.supplier_notes || '',
        };
      });
      setDrafts(d);
    } catch (e) {
      toast.error('Error cargando pedidos: ' + (e.response?.data?.detail || e.message));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const setDraft = (id, k, v) => setDrafts((p) => ({ ...p, [id]: { ...p[id], [k]: v } }));

  const saveOrder = async (id) => {
    setSavingId(id);
    try {
      await axios.patch(`${API}/supplier-orders/${id}`, drafts[id]);
      toast.success('Pedido actualizado');
      load();
    } catch (e) {
      toast.error('Error al guardar: ' + (e.response?.data?.detail || e.message));
    } finally {
      setSavingId(null);
    }
  };

  const openEmail = async (id) => {
    try {
      const r = await axios.get(`${API}/supplier-orders/${id}/email-preview`);
      setEmailModal({ orderId: id, to: r.data.to || '', subject: r.data.subject || '', body: r.data.body || '' });
    } catch (e) {
      toast.error('Error preparando email: ' + (e.response?.data?.detail || e.message));
    }
  };

  const sendEmail = async () => {
    if (!emailModal?.to?.trim()) { toast.error('Indica el email del proveedor'); return; }
    setSending(true);
    try {
      const r = await axios.post(`${API}/supplier-orders/${emailModal.orderId}/send-email`, {
        to: emailModal.to, subject: emailModal.subject, body: emailModal.body,
      });
      if (r.data?.sent) {
        toast.success('Email enviado al proveedor');
        setEmailModal(null);
        load();
      } else {
        toast.error('No se pudo enviar el email');
      }
    } catch (e) {
      toast.error('Error al enviar: ' + (e.response?.data?.detail || e.message));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="supplier-orders-panel">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2 text-slate-800">
              <Truck size={20} className="text-blue-600" />
              Pedidos de Proveedor (Productos Manuales)
            </CardTitle>
            <p className="text-sm text-slate-500 mt-1">
              Pedidos pagados que contienen productos manuales. Gestiona la compra al proveedor, el envío y el margen.
            </p>
          </div>
          <Button data-testid="supplier-refresh-btn" onClick={load} variant="outline" disabled={loading}>
            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
          </Button>
        </CardHeader>
        <CardContent>
          {orders.length === 0 ? (
            <p className="text-sm text-slate-500 py-8 text-center" data-testid="supplier-empty">
              No hay pedidos de productos manuales todavía. Aparecerán aquí cuando un cliente compre uno.
            </p>
          ) : (
            <div className="space-y-4">
              {orders.map((o) => {
                const d = drafts[o.id] || {};
                const meta = statusMeta(o.supplier_status);
                const customItems = (o.items || []).filter((i) => i.is_custom);
                return (
                  <div key={o.id} className="border rounded-lg p-4 bg-white" data-testid={`supplier-order-${o.id}`}>
                    <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                      <div>
                        <span className="font-semibold text-slate-800">Pedido #{o.id}</span>
                        <span className="text-xs text-slate-400 ml-2">{o.customer_email}</span>
                      </div>
                      <Badge className={`${meta.color} hover:${meta.color}`}>{meta.label}</Badge>
                    </div>

                    {/* Items + finanzas */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-3">
                      <div className="text-sm">
                        <p className="text-xs uppercase text-slate-400 mb-1">Productos manuales</p>
                        {customItems.map((it, idx) => (
                          <div key={idx} className="text-slate-700">
                            • {it.name} <span className="text-slate-400">x{it.quantity}</span>
                            {it.supplier_name && <span className="text-slate-400"> · {it.supplier_name}</span>}
                            {it.supplier_sku && <span className="text-slate-400"> · SKU {it.supplier_sku}</span>}
                          </div>
                        ))}
                        {o.shipping_address && (
                          <p className="text-xs text-slate-400 mt-2">
                            Envío a: {o.shipping_name} — {o.shipping_address.city}, {o.shipping_address.country}
                          </p>
                        )}
                      </div>
                      <div className="text-sm bg-slate-50 rounded p-3">
                        <div className="flex justify-between"><span className="text-slate-500">Ingreso (venta)</span><span className="font-medium">{money(o.fin_revenue)}</span></div>
                        <div className="flex justify-between"><span className="text-slate-500">Coste {o.fin_cost_known ? '' : '(parcial)'}</span><span className="text-slate-700">-{money(o.fin_cost)}</span></div>
                        <div className="flex justify-between"><span className="text-slate-500">Comisión Stripe</span><span className="text-slate-700">-{money(o.fin_stripe_fee)}</span></div>
                        <div className="flex justify-between border-t mt-1 pt-1 font-semibold">
                          <span>Margen neto</span>
                          <span className={o.fin_net_margin >= 0 ? 'text-emerald-700' : 'text-red-600'}>{money(o.fin_net_margin)}</span>
                        </div>
                        {!o.fin_cost_known && <p className="text-[11px] text-amber-600 mt-1">⚠️ Falta coste en algún producto; margen aproximado.</p>}
                      </div>
                    </div>

                    {/* Gestión */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-2 items-end">
                      <div>
                        <Label className="text-xs">Estado</Label>
                        <select
                          data-testid={`supplier-status-${o.id}`}
                          value={d.supplier_status}
                          onChange={(e) => setDraft(o.id, 'supplier_status', e.target.value)}
                          className="w-full border border-slate-300 rounded-md px-2 py-2 text-sm bg-white"
                        >
                          {STATUSES.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
                        </select>
                      </div>
                      <div>
                        <Label className="text-xs">Ref. pedido proveedor</Label>
                        <Input data-testid={`supplier-ref-${o.id}`} value={d.supplier_ref} onChange={(e) => setDraft(o.id, 'supplier_ref', e.target.value)} placeholder="Nº pedido" />
                      </div>
                      <div>
                        <Label className="text-xs">Tracking</Label>
                        <Input data-testid={`supplier-tracking-${o.id}`} value={d.supplier_tracking} onChange={(e) => setDraft(o.id, 'supplier_tracking', e.target.value)} placeholder="Nº seguimiento" />
                      </div>
                      <div>
                        <Label className="text-xs">Transportista</Label>
                        <Input data-testid={`supplier-carrier-${o.id}`} value={d.supplier_carrier} onChange={(e) => setDraft(o.id, 'supplier_carrier', e.target.value)} placeholder="UPS, Fedex..." />
                      </div>
                      <div className="md:col-span-2 lg:col-span-4">
                        <Label className="text-xs">Notas</Label>
                        <Textarea data-testid={`supplier-notes-${o.id}`} value={d.supplier_notes} onChange={(e) => setDraft(o.id, 'supplier_notes', e.target.value)} rows={1} placeholder="Notas internas..." />
                      </div>
                    </div>

                    <div className="flex gap-2 mt-3">
                      <Button data-testid={`supplier-save-${o.id}`} onClick={() => saveOrder(o.id)} disabled={savingId === o.id} size="sm" className="bg-slate-900 hover:bg-slate-800 text-white">
                        {savingId === o.id ? <RefreshCw size={14} className="animate-spin mr-1" /> : <Save size={14} className="mr-1" />} Guardar
                      </Button>
                      <Button data-testid={`supplier-email-${o.id}`} onClick={() => openEmail(o.id)} size="sm" variant="outline" className="text-blue-600 border-blue-200 hover:bg-blue-50">
                        <Mail size={14} className="mr-1" /> Email al proveedor
                      </Button>
                      {o.supplier_email_sent_at && (
                        <span className="inline-flex items-center text-xs text-emerald-600 ml-1">
                          <PackageCheck size={14} className="mr-1" /> Email enviado
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Email preview modal */}
      {emailModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" data-testid="supplier-email-modal">
          <div className="bg-white rounded-lg max-w-lg w-full p-5 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-3">
              <h4 className="font-semibold text-slate-800 flex items-center gap-2"><Mail size={18} className="text-blue-600" /> Email al proveedor</h4>
              <button onClick={() => setEmailModal(null)} className="text-slate-400 hover:text-slate-700" data-testid="supplier-email-close"><X size={18} /></button>
            </div>
            <p className="text-xs text-slate-500 mb-3">Revisa y edita antes de enviar. Se envía desde tu cuenta de DRYWALL DESING LLC.</p>
            <div className="space-y-3">
              <div>
                <Label className="text-xs">Para (email del proveedor)</Label>
                <Input data-testid="supplier-email-to" type="email" value={emailModal.to} onChange={(e) => setEmailModal({ ...emailModal, to: e.target.value })} placeholder="pedidos@proveedor.com" />
              </div>
              <div>
                <Label className="text-xs">Asunto</Label>
                <Input data-testid="supplier-email-subject" value={emailModal.subject} onChange={(e) => setEmailModal({ ...emailModal, subject: e.target.value })} />
              </div>
              <div>
                <Label className="text-xs">Mensaje</Label>
                <Textarea data-testid="supplier-email-body" value={emailModal.body} onChange={(e) => setEmailModal({ ...emailModal, body: e.target.value })} rows={12} className="font-mono text-xs" />
              </div>
            </div>
            <div className="flex gap-2 mt-4">
              <Button data-testid="supplier-email-send" onClick={sendEmail} disabled={sending} className="bg-blue-600 hover:bg-blue-700 text-white">
                {sending ? <RefreshCw size={14} className="animate-spin mr-1" /> : <Send size={14} className="mr-1" />} Enviar
              </Button>
              <Button onClick={() => setEmailModal(null)} variant="outline">Cancelar</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
